import { formatEquation, type Equation } from '../../shared/algebra.ts';
import type { AudioSegment } from '../components/NarrationPlayer.tsx';

export type Focus =
  | 'x'
  | 'coefficient'
  | 'constant'
  | 'total'
  | 'equals'
  | 'left'
  | 'right'
  | 'both'
  | 'rate'
  | 'hours'
  | 'result';
export type SceneKind =
  | 'intro'
  | 'equality'
  | 'subtract'
  | 'divide'
  | 'check'
  | 'story'
  | 'practice';
export type SceneFrame = {
  kind: SceneKind;
  phase: number;
  progress: number;
  focus: Focus;
  caption: string;
  word: string;
  equation: string;
};
export type ReplyFocusResult = {
  focus: Focus;
  word: string;
  normalizedToken: string;
};

type Word = AudioSegment['words'][number];
type Cue = { phrase: string; phase: number; focus: Focus; caption: string };
type CueDefinition = { kind: SceneKind; cues: Cue[] };

const numbers: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5',
  six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
  eleven: '11', twelve: '12', thirteen: '13', fourteen: '14',
  fifteen: '15', sixteen: '16', seventeen: '17', eighteen: '18',
  nineteen: '19', twenty: '20',
};

const normalized = (text: string): string[] =>
  text.toLowerCase().replace(/[$.,:;!?]/g, '').replace(/(\d)x\b/g, '$1 x')
    .split(/\s+/).filter(Boolean).map((word) => numbers[word] ?? word);

/** Cues are anchored to words in the actual cached MP3 alignment, never wall time. */
export function phraseTime(words: readonly Word[], phrase: string, fallback = 0): number {
  const flat = words.flatMap((word) =>
    normalized(word.text).map((text) => ({ text, at: word.startMs })),
  );
  const wanted = normalized(phrase);
  const index = flat.findIndex((_, candidate) =>
    wanted.every((word, offset) => flat[candidate + offset]?.text === word),
  );
  return index < 0 ? fallback : flat[index].at;
}

export function spokenWord(words: readonly Word[], position: number): string {
  return words.find((word) => position >= word.startMs && position < word.endMs)?.text ?? '';
}

const cues: Record<string, CueDefinition> = {
  'meet-the-unknown': { kind: 'intro', cues: [
    { phrase: 'Let us', phase: 0, focus: 'both', caption: 'An equation is a relationship we can explore.' },
    { phrase: 'two x plus', phase: 1, focus: 'coefficient', caption: '2x means two copies of the same unknown.' },
    { phrase: 'The letter x', phase: 1, focus: 'x', caption: 'Each x stands for the same unknown number.' },
    { phrase: 'two x means', phase: 2, focus: 'x', caption: 'Two equal groups: x + x.' },
    { phrase: 'The four means', phase: 3, focus: 'constant', caption: 'Four more units are added to those groups.' },
    { phrase: 'Their size', phase: 3, focus: 'x', caption: 'Tile size does not tell us the unknown value.' },
  ] },
  'meaning-of-equals': { kind: 'equality', cues: [
    { phrase: 'Look at', phase: 0, focus: 'equals', caption: 'Equals means the same value.' },
    { phrase: 'expression on the left', phase: 1, focus: 'left', caption: 'Compare the entire left expression…' },
    { phrase: 'expression on the right', phase: 2, focus: 'right', caption: '…with the entire right expression.' },
    { phrase: 'same value', phase: 3, focus: 'both', caption: 'Two expressions. The same value.' },
    { phrase: 'value for x', phase: 3, focus: 'x', caption: 'Find an x that makes the relationship true.' },
    { phrase: 'keeping the same solution', phase: 3, focus: 'both', caption: 'A reversible change to both sides preserves the solution.' },
  ] },
  'subtract-from-both': { kind: 'subtract', cues: [
    { phrase: 'The four', phase: 0, focus: 'constant', caption: 'Start with 2x + 4 = 10.' },
    { phrase: 'Subtract four', phase: 1, focus: 'both', caption: 'Write −4 on BOTH sides. Nothing crosses the equals sign.' },
    { phrase: 'On the left', phase: 2, focus: 'constant', caption: 'On the left, +4 and −4 make zero.' },
    { phrase: 'On the right', phase: 3, focus: 'total', caption: 'On the right, take four units away from ten.' },
    { phrase: 'Now two x', phase: 4, focus: 'both', caption: 'The remaining equation is 2x = 6.' },
    { phrase: 'only one side', phase: 4, focus: 'equals', caption: 'Changing only one side would not preserve this relationship.' },
  ] },
  'share-equal-groups': { kind: 'divide', cues: [
    { phrase: 'Two equal', phase: 0, focus: 'x', caption: 'Two equal x groups have a total of six.' },
    { phrase: 'divide both', phase: 1, focus: 'both', caption: 'Divide BOTH entire sides by two.' },
    { phrase: 'Two x divided', phase: 2, focus: 'left', caption: '2x ÷ 2 leaves one x group.' },
    { phrase: 'six divided', phase: 3, focus: 'right', caption: 'Six shared into two equal groups gives three in each.' },
    { phrase: 'So x equals', phase: 4, focus: 'result', caption: 'One x group has three units: x = 3.' },
    { phrase: 'Division must', phase: 4, focus: 'both', caption: 'Divide every term; never divide by zero.' },
  ] },
  'check-the-original': { kind: 'check', cues: [
    { phrase: 'Finding a number', phase: 0, focus: 'both', caption: 'Return to the ORIGINAL equation: 2x + 4 = 10.' },
    { phrase: 'Replace each x', phase: 1, focus: 'x', caption: 'Replace x with 3. Keep the rest of the original unchanged.' },
    { phrase: 'two times three', phase: 2, focus: 'left', caption: '2 × 3 is 6.' },
    { phrase: 'six plus four', phase: 3, focus: 'constant', caption: '6 + 4 is 10.' },
    { phrase: 'Both sides', phase: 4, focus: 'equals', caption: '10 = 10. The original relationship is true.' },
  ] },
  'connect-to-a-story': { kind: 'story', cues: [
    { phrase: 'Here is', phase: 0, focus: 'both', caption: 'A fictional scooter rental: connect each story part to a symbol.' },
    { phrase: 'costs four', phase: 1, focus: 'constant', caption: 'The $4 starting charge happens once.' },
    { phrase: 'then two', phase: 2, focus: 'rate', caption: '$2 per hour × x hours gives the hourly cost.' },
    { phrase: 'The total', phase: 3, focus: 'total', caption: 'The receipt total is $10.' },
    { phrase: 'Let x', phase: 4, focus: 'hours', caption: 'x represents the number of hours.' },
    { phrase: 'Two times x', phase: 5, focus: 'both', caption: 'Move the story parts into 2x + 4 = 10.' },
    { phrase: 'Our solution', phase: 6, focus: 'result', caption: '3 means 3 hours: $2 × 3 + $4 = $10.' },
  ] },
  'your-turn': { kind: 'practice', cues: [
    { phrase: 'You have seen', phase: 0, focus: 'both', caption: 'Your practice uses different numbers.' },
    { phrase: 'three x plus', phase: 1, focus: 'x', caption: 'Your equation is 3x + 6 = 18.' },
    { phrase: 'You choose', phase: 2, focus: 'both', caption: 'Try a move, check it, and explain why it works.' },
  ] },
};

const numericFocus: Record<SceneKind, Partial<Record<string, Focus>>> = {
  intro: { '2': 'coefficient', '4': 'constant', '10': 'total' },
  equality: { '2': 'coefficient', '4': 'constant', '10': 'total' },
  subtract: { '2': 'coefficient', '4': 'constant', '6': 'total', '10': 'total' },
  divide: { '2': 'coefficient', '3': 'result', '6': 'right' },
  check: { '2': 'coefficient', '3': 'result', '4': 'constant', '6': 'left', '10': 'total' },
  story: { '2': 'rate', '3': 'result', '4': 'constant', '10': 'total' },
  practice: { '3': 'coefficient', '6': 'constant', '18': 'total' },
};

const focusForSpokenWord = (kind: SceneKind, currentWord: string, fallback: Focus): Focus => {
  const tokens = normalized(currentWord);
  if (tokens.includes('x')) return 'x';
  if (tokens.includes('equals') || tokens.includes('equal')) return 'equals';
  const numeric = tokens.find((token) => /^\d+$/.test(token));
  return (numeric && numericFocus[kind][numeric]) || fallback;
};

const equationForFrame = (kind: SceneKind, phase: number): string => {
  if (kind === 'subtract') return phase >= 4 ? '2x = 6' : '2x + 4 = 10';
  if (kind === 'divide') return phase >= 4 ? 'x = 3' : '2x = 6';
  if (kind === 'check') {
    if (phase >= 4) return '10 = 10';
    if (phase >= 3) return '6 + 4 = 10';
    if (phase >= 1) return '2 × 3 + 4 = 10';
    return '2x + 4 = 10';
  }
  return kind === 'practice' ? '3x + 6 = 18' : '2x + 4 = 10';
};

export function lessonFrame(
  id: string,
  segment: AudioSegment | undefined,
  position: number,
  reduced = false,
): SceneFrame {
  const definition = cues[id] ?? cues['meet-the-unknown'];
  const words = segment?.words ?? [];
  const boundedPosition = Math.max(0, position);
  const resolved = definition.cues.map((cue, index) => ({
    ...cue,
    at: phraseTime(words, cue.phrase, index === 0 ? 0 : Infinity),
  }));
  const active = resolved.filter((cue) => boundedPosition >= cue.at).at(-1) ?? resolved[0];
  const phaseStart = resolved.find((cue) => cue.phase === active.phase)?.at ?? active.at;
  const nextPhaseStart = resolved.find(
    (cue) => cue.phase > active.phase && Number.isFinite(cue.at),
  )?.at;
  const phaseEnd = Math.max(
    phaseStart + 1,
    nextPhaseStart ?? segment?.durationMs ?? phaseStart + 650,
  );
  const word = spokenWord(words, boundedPosition);
  const focus = focusForSpokenWord(definition.kind, word, active.focus);
  const progress = reduced
    ? 1
    : Math.min(1, Math.max(0, (boundedPosition - phaseStart) / (phaseEnd - phaseStart)));
  return {
    kind: definition.kind,
    phase: active.phase,
    progress,
    focus,
    caption: active.caption,
    word,
    equation: equationForFrame(definition.kind, active.phase),
  };
}

const rationalMagnitude = (value: string): string => value.startsWith('-') ? value.slice(1) : value;

/**
 * Live prose can direct attention, but cannot mutate or grade the equation.
 * The fifth argument must contain timings from the synthesized audio. Without
 * an active aligned word, the result stays neutral; duration is retained only
 * for compatibility with the previous call signature.
 */
export function replyFocus(
  text: string,
  position: number,
  duration: number,
  equation: Equation,
  words?: readonly Word[],
): ReplyFocusResult {
  void text;
  void duration;
  if (!words?.length || !Number.isFinite(position) || position < 0)
    return { focus: 'both', word: '', normalizedToken: '' };
  const index = words.findIndex(
    (candidate) => position >= candidate.startMs && position < candidate.endMs,
  );
  if (index < 0) return { focus: 'both', word: '', normalizedToken: '' };

  const word = words[index].text;
  const tokens = normalized(word);
  const context = words.slice(Math.max(0, index - 4), index + 2)
    .flatMap((candidate) => normalized(candidate.text)).join(' ');
  let focus: Focus = 'both';
  if (/\bhours?\b/.test(context)) focus = 'hours';
  else if (/\b(?:start|fixed|constant)\b/.test(context)) focus = 'constant';
  else if (/\b(?:rate|per)\b/.test(context)) focus = 'rate';
  else if (/\b(?:total|receipt|right)\b/.test(context)) focus = 'total';
  else if (/\b(?:equals?|equal)\b/.test(context)) focus = 'equals';
  else if (tokens.includes('x') || /\b(?:unknown|group)\b/.test(context)) focus = 'x';
  else {
    const numeric = tokens.find((token) => /^\d+(?:\/\d+)?$/.test(token));
    const variableSide = equation.left.x !== '0' ? equation.left : equation.right;
    const totalSide = equation.left.x !== '0' ? equation.right : equation.left;
    if (numeric && rationalMagnitude(variableSide.constant) !== '0' &&
        numeric === rationalMagnitude(variableSide.constant)) focus = 'constant';
    else if (numeric && numeric === rationalMagnitude(variableSide.x)) focus = 'rate';
    else if (numeric && numeric === rationalMagnitude(totalSide.constant)) focus = 'total';
  }
  return { focus, word, normalizedToken: tokens.join(' ') };
}

export const sceneCueDefinitions = cues;
export const sceneEquationText = formatEquation;
